import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { TinyTalkLogoFull, TinyTalkLogo } from '@/components/app/Logo'
import { ChildIllustration } from '@/components/app/ChildAvatar'
import { Link } from '@tanstack/react-router'
import { CATEGORY_CONFIG } from '@/constants/dashboard.constants'
import { QuestionnaireCategory } from '@/types/dashboard.types'

export const LandingPage = () => {
  const features = [
    {
      icon: '🎯',
      title: 'Questionnaire Review',
      description:
        'Checks your answers, makes sure all questions are complete, and organizes them in a clear format.',
      bgClass: 'bg-teal-100',
    },
    {
      icon: '🗣️',
      title: 'Language Understanding',
      description:
        'Looks at how your child communicates based on your answers and highlights important details.',
      bgClass: 'bg-sky-100',
    },
    {
      icon: '✅',
      title: 'Parent Answers Summary',
      description:
        'Summarizes your responses so professionals can quickly see patterns and concerns.',
      bgClass: 'bg-cyan-100',
    },
    {
      icon: '📊',
      title: 'Child Progress Report',
      bgClass: 'bg-blue-100',
      description:
        'Creates a simple report with your child’s risk level and next-step suggestions.',
    },
  ]

  const domains = [
    {
      category: QuestionnaireCategory.SPEECH_SOUND,
      description: 'Evaluates how clearly your child produces sounds and words',
    },
    {
      category: QuestionnaireCategory.LANGUAGE_COMPREHENSION,
      description: 'Assesses how well your child understands spoken language',
    },
    {
      category: QuestionnaireCategory.EXPRESSIVE_LANGUAGE,
      description: 'Measures how your child uses words and sentences to communicate',
    },
    {
      category: QuestionnaireCategory.BEHAVIORAL,
      description: 'Looks at gestures, play, and how your child responds when others misunderstand',
    },
  ]

  const howItWorks = [
    {
      step: '1',
      icon: '📝',
      title: 'Register & Add Child',
      description: "Create your account and add your child's profile information",
      bgClass: 'bg-teal-100',
      stepColor: 'text-primary',
    },
    {
      step: '2',
      icon: '📋',
      title: 'Complete Assessment',
      description: "Answer 40 comprehensive questions about your child's development",
      bgClass: 'bg-blue-100',
      stepColor: 'text-blue-600',
    },
    {
      step: '3',
      icon: '🤖',
      title: 'Smart Scoring',
      description: 'TinyTalk analyzes responses using a validated scoring system',
      bgClass: 'bg-amber-100',
      stepColor: 'text-amber-600',
    },
    {
      step: '4',
      icon: '📊',
      title: 'Get Results',
      description: 'Receive detailed report with risk level and recommendations',
      bgClass: 'bg-emerald-100',
      stepColor: 'text-emerald-600',
    },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 via-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-gradient-to-r from-teal-50/95 via-white/95 to-blue-50/95 backdrop-blur-md border-b border-transparent shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <TinyTalkLogoFull />
            <div className="flex gap-3">
              <Link to="/auth/login">
                <Button variant="ghost" className="font-medium text-slate-800 hover:text-primary hover:bg-primary/10">
                  Login
                </Button>
              </Link>
              <Link to="/auth/register">
                <Button variant="default">Get Started</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6 animate-in fade-in slide-in-from-left duration-700">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium animate-in fade-in duration-500">
              <TinyTalkLogo className="w-6 h-6" />
              <span>Empowering Tiny Voices</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold leading-tight text-slate-900 animate-in fade-in slide-in-from-bottom duration-700">
              Fun & Speech Self-Assessments
            </h1>
            <p className="text-xl leading-relaxed text-slate-700 animate-in fade-in slide-in-from-bottom duration-700">
              Early detection of speech and language delays in Filipino children ages 2-5 using
              guided questionnaires and a standardized scoring system.
            </p>

            <div className="flex gap-4 animate-in fade-in slide-in-from-bottom duration-700">
              <Link to="/auth/register">
                <Button size="lg" className="gap-2 text-lg px-8">
                  <span>Get Started</span>
                  <span>→</span>
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="gap-2 text-lg px-8 border-transparent bg-sky-50 text-sky-700 hover:bg-sky-100">
                <span>Learn More</span>
              </Button>
            </div>
            <div className="flex gap-8 pt-4">
              <div>
                <div className="text-3xl font-bold text-primary">40</div>
                <div className="text-sm font-medium text-slate-700">Comprehensive Questions</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600">15-20</div>
                <div className="text-sm font-medium text-slate-700">Minutes to Complete</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-cyan-600">4</div>
                <div className="text-sm font-medium text-slate-700">Developmental Domains</div>
              </div>
            </div>
          </div>
          <div className="relative animate-in fade-in slide-in-from-right duration-700">
            <div className="absolute inset-0 bg-gradient-to-br from-teal-200 via-cyan-200 to-blue-200 rounded-3xl transform rotate-3 opacity-20" />
            <ChildIllustration className="relative w-full h-auto animate-fade-in" />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-teal-100/90 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-in fade-in slide-in-from-bottom duration-700">
            <h2 className="text-4xl font-bold mb-4 text-slate-900 animate-in fade-in duration-500">How It Works</h2>
            <p className="text-xl leading-relaxed text-slate-700 max-w-2xl mx-auto animate-in fade-in duration-700">
              Simple 4-step process to assess your child's speech and language development
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 animate-in slide-in-from-bottom duration-700">
            {howItWorks.map((item) => (
              <Card key={item.step} className={`relative overflow-hidden border-transparent ${item.bgClass} animate-in fade-in slide-in-from-bottom duration-700 hover:shadow-lg transition-all`}>
                <div className={`absolute top-4 right-4 text-6xl font-bold opacity-30 ${item.stepColor}`}>
                  {item.step}
                </div>
                <CardContent className="pt-6 relative z-10">
                  <div className="text-4xl mb-4">{item.icon}</div>
                  <h3 className="text-xl font-semibold mb-2 text-slate-900">{item.title}</h3>
                  <p className="text-slate-700 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-blue-100/90 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-in fade-in slide-in-from-bottom duration-700">
            <h2 className="text-4xl font-bold mb-4 text-slate-900 animate-in fade-in duration-500">Core Features</h2>
            <p className="text-xl leading-relaxed text-slate-700 max-w-2xl mx-auto animate-in fade-in duration-700">
              Built on a consistent, evidence-informed scoring system for reliable assessments
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 animate-in slide-in-from-bottom duration-700">
            {features.map((feature, index) => (
              <Card key={index} className={`border-transparent ${feature.bgClass} hover:shadow-lg transition-all animate-in fade-in slide-in-from-bottom duration-700`}>
                <CardContent className="pt-6">
                  <div className="flex gap-4">
                    <div className="text-4xl">{feature.icon}</div>
                    <div className="space-y-2">
                      <h3 className="text-xl font-semibold text-slate-900">{feature.title}</h3>
                      <p className="text-slate-700 leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Assessment Domains */}
      <section className="bg-violet-100/90 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-in fade-in slide-in-from-bottom duration-700">
            <h2 className="text-4xl font-bold mb-4 text-slate-900 animate-in fade-in duration-500">Assessment Domains</h2>
            <p className="text-xl leading-relaxed text-slate-700 max-w-2xl mx-auto animate-in fade-in duration-700">
              Comprehensive evaluation across 4 key developmental areas
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto animate-in slide-in-from-bottom duration-700">
            {domains.map((domain) => {
              const config = CATEGORY_CONFIG[domain.category]
              const DomainIcon = config.Icon
              return (
                <Card
                  key={domain.category}
                  className="border-transparent hover:shadow-lg transition-all animate-in fade-in slide-in-from-bottom duration-700"
                  style={{ backgroundColor: config.color + '30' }}
                >
                  <CardContent className="pt-6">
                    <div className="flex gap-4">
                      <div
                        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl"
                        style={{ backgroundColor: `${config.color}40` }}
                      >
                        <DomainIcon className="h-8 w-8" style={{ color: config.color }} aria-hidden />
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-xl font-semibold" style={{ color: config.color }}>
                          {config.label}
                        </h3>
                        <p className="text-slate-700 leading-relaxed">{domain.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      </section>

      {/* Risk Levels */}
      <section className="bg-yellow-100/90 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-in fade-in slide-in-from-bottom duration-700">
            <h2 className="text-4xl font-bold mb-4 text-slate-900 animate-in fade-in duration-500">Risk Level Assessment</h2>
            <p className="text-xl leading-relaxed text-slate-700 max-w-2xl mx-auto animate-in fade-in duration-700">
              Clear, color-coded results with actionable recommendations
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto animate-in slide-in-from-bottom duration-700">
            <Card className="border-transparent bg-emerald-100 animate-in slide-in-from-left duration-700">
              <CardContent className="pt-6 text-center">
                <div className="text-5xl mb-4">✓</div>
                <h3 className="text-2xl font-bold text-emerald-600 mb-2">Low Risk</h3>
                <p className="text-emerald-700">Development appears to be on track</p>
              </CardContent>
            </Card>
            <Card className="border-transparent bg-yellow-100 animate-in fade-in slide-in-from-bottom duration-700">
              <CardContent className="pt-6 text-center">
                <div className="text-5xl mb-4">⚠</div>
                <h3 className="text-2xl font-bold text-yellow-600 mb-2">Mid Risk</h3>
                <p className="text-yellow-700">Some areas may need attention</p>
              </CardContent>
            </Card>
            <Card className="border-transparent bg-red-100 animate-in slide-in-from-right duration-700">
              <CardContent className="pt-6 text-center">
                <div className="text-5xl mb-4">!</div>
                <h3 className="text-2xl font-bold text-red-600 mb-2">High Risk</h3>
                <p className="text-red-700">Professional evaluation recommended</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-teal-500 to-blue-500 py-16 md:py-24 text-white animate-in fade-in slide-in-from-bottom duration-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 animate-in fade-in duration-500">
            Ready to Start Your Assessment?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto animate-in fade-in duration-700">
            Join parents who trust TinyTalk for early speech and language screening
          </p>
          <div className="flex gap-4 justify-center animate-in slide-in-from-bottom duration-700">
            <Link to="/auth/register">
              <Button size="lg" variant="secondary" className="gap-2 text-lg px-8">
                <span>Get Started Free</span>
                <span>→</span>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="container mx-auto px-4">
          {/*
            Mobile: brand full width, then Product | Resources | Legal in 3 columns.
            md+: single row, 4 equal columns (md:contents exposes the three sections to the parent grid).
          */}
          <div className="mb-8 flex flex-col gap-8 animate-in fade-in slide-in-from-bottom duration-700 md:grid md:grid-cols-4 md:gap-8">
            <div className="animate-in fade-in duration-500">
              <div className="mb-4">
                <TinyTalkLogo className="w-10 h-10" />
              </div>
              <p className="text-sm">
                Early detection of speech and language delays in Filipino children ages 2-5.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2 sm:gap-4 md:contents">
              <div className="min-w-0 animate-in fade-in duration-700">
                <h4 className="mb-2 font-semibold text-white sm:mb-4">Product</h4>
                <ul className="space-y-1.5 text-xs sm:space-y-2 sm:text-sm">
                  <li>
                    <a href="#" className="hover:text-white">
                      Features
                    </a>
                  </li>
                  <li>
                    <a href="#" className="hover:text-white">
                      How It Works
                    </a>
                  </li>
                  <li>
                    <a href="#" className="hover:text-white">
                      Pricing
                    </a>
                  </li>
                </ul>
              </div>
              <div className="min-w-0 animate-in fade-in duration-700">
                <h4 className="mb-2 font-semibold text-white sm:mb-4">Resources</h4>
                <ul className="space-y-1.5 text-xs sm:space-y-2 sm:text-sm">
                  <li>
                    <a href="#" className="hover:text-white">
                      About Us
                    </a>
                  </li>
                  <li>
                    <a href="#" className="hover:text-white">
                      Research
                    </a>
                  </li>
                  <li>
                    <a href="#" className="hover:text-white">
                      FAQ
                    </a>
                  </li>
                </ul>
              </div>
              <div className="min-w-0 animate-in fade-in duration-700">
                <h4 className="mb-2 font-semibold text-white sm:mb-4">Legal</h4>
                <ul className="space-y-1.5 text-xs sm:space-y-2 sm:text-sm">
                  <li>
                    <a href="#" className="hover:text-white">
                      Privacy Policy
                    </a>
                  </li>
                  <li>
                    <a href="#" className="hover:text-white">
                      Terms of Service
                    </a>
                  </li>
                  <li>
                    <a href="#" className="hover:text-white">
                      Contact
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-sm text-center">
            <p>© 2026 TinyTalk. All rights reserved. Speech & Language Assessment System</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
